import { Form, Formik, FormikProps } from 'formik';
import { FC, FormEvent, useEffect, useMemo, useState } from 'react';

import { CheckoutDescription } from '@/checkout/components/CheckoutDescription/CheckoutDescription';
import { MICROCOPY } from '@/data/microcopy/microcopyDictionary';
import { BasicModal } from '@/general/components/BasicModal/BasicModal';
import { Button } from '@/general/components/Button/Button';
import { InputFormik } from '@/general/components/forms/Input/InputFormik';
import { InputSelectFormik } from '@/general/components/forms/Input/InputSelectFormik';
import { ResultCard } from '@/personalised-quote/components/ResultCard/ResultCard';
import { getMicrocopy } from '@/stores/microcopyStore';
import { isEmpty } from '@/utils/isEmpty';

import styles from './PersonalizedQuoteForm.module.scss';
import {
  LoanDetailsFormValidation,
  LoanDetailsFormValues,
  PersonalizedQuoteFormProps,
  buildYears,
} from './PersonalizedQuoteForm.type';

const initialValues: LoanDetailsFormValues = {
  estimatedPurchasePrice: 40000,
  depositAmount: 1200,
  preferredBuildYear: '',
};

export const LoanDetailsForm: FC<PersonalizedQuoteFormProps<LoanDetailsFormValues>> = ({
  onBack,
  onSubmit,
  values,
  fetchCarsCount,
}) => {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [countMatchingCars, setCountMatchingCars] = useState(0);

  const handleOpenModal = () => {
    setIsOpenModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
  };

  const resultCard = useMemo(
    () => (countMatchingCars > 0 ? <ResultCard carsFound={countMatchingCars.toString()} /> : null),
    [countMatchingCars],
  );

  useEffect(() => {
    if (values.preferredBuildYear && values.estimatedPurchasePrice && fetchCarsCount) {
      fetchCarsCount(values.preferredBuildYear, values.estimatedPurchasePrice.toString()).then((value) => {
        setCountMatchingCars(value);
      });
    }
  }, []);

  const onChange = (e: FormEvent<HTMLFormElement>, props: FormikProps<LoanDetailsFormValues>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    const newValues: LoanDetailsFormValues = { ...props.values, [name]: value };
    props.validateForm(newValues).then((errors) => {
      if (isEmpty(errors) && fetchCarsCount) {
        fetchCarsCount(newValues.preferredBuildYear, newValues.estimatedPurchasePrice.toString()).then((value) => {
          setCountMatchingCars(value);
        });
      }
    });
  };

  return (
    <>
      <CheckoutDescription
        title={getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_LOAN_DETAILS_FORM_HEADER)}
        onBack={onBack}
      />
      <div className={styles.loanDetails}>
        <Formik
          initialValues={{ ...initialValues, ...values }}
          onSubmit={onSubmit}
          validationSchema={LoanDetailsFormValidation}
        >
          {(props) => (
            <Form
              className={styles.form}
              data-testid="personalised-quote-form"
              onChange={(e) => {
                onChange(e, props);
              }}
            >
              <InputFormik
                id="estimatedPurchasePrice"
                name="estimatedPurchasePrice"
                label="Est. purchase price"
                iconLeft="$"
                data-testid="personalised-quote-form-est-purchase-price"
              />
              <InputFormik
                id="depositAmount"
                name="depositAmount"
                label="Deposit"
                iconLeft="$"
                data-testid="personalised-quote-form-deposit"
              />
              <InputSelectFormik
                name="preferredBuildYear"
                id="preferredBuildYear"
                label="Preferred build year"
                size="small"
                data-testid="personalised-quote-form-preferred-build-year"
                options={buildYears.map((item) => ({
                  label: item.label,
                  value: item.value,
                }))}
                allowEmpty
              />
              <div className={styles.description}>
                {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_LOAN_DETAILS_FORM_DESCRIPTION)}
                <Button variant="link" onClick={handleOpenModal} size="small">
                  {getMicrocopy(MICROCOPY.HYGIENE_WHY_CARMA_CTA)}
                </Button>
              </div>
              <BasicModal
                id="loan-details-learn-more-modal"
                isOpen={isOpenModal}
                onClose={handleCloseModal}
                title={getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_LOAN_DETAILS_FORM_MODAL_TITLE)}
              >
                {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_LOAN_DETAILS_FORM_MODAL_DESCRIPTION)}
              </BasicModal>
              <footer className={styles.footer}>
                <Button type="submit" isLoading={props.isSubmitting} fullWidth data-testid="submit">
                  {getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_LOAN_DETAILS_FORM_CTA)}
                </Button>
              </footer>
              {props.values.preferredBuildYear && <div className={styles.cardResult}>{resultCard}</div>}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};
